import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Overview } from "../../api/EarningsOverview";
import { FONTS } from "../../constants/styles";
import { ANALYTICS_ENABLED } from "../../constants/features";

/** Shared frosted-glass surface style — light tint that works on any brand color */
const GLASS_SX = {
    bgcolor: "rgba(255,255,255,0.18)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    border: "1px solid rgba(255,255,255,0.3)",
} as const;

const CARD_SX = {
    ...GLASS_SX,
    borderRadius: "16px",
    px: 2,
    py: 1.75,
} as const;

interface DashboardHeroProps {
    overview?: Overview;
    setupProgress?: { done: number; total: number };
}

const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
};

/** Format a minute-sum as compact hours: 0h, 2h, 1.5h. */
const formatHours = (minutes: number): string => {
    const hours = minutes / 60;
    return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
};

const MoneyHeadline = ({ label, amount }: { label: string; amount: number }) => (
    <Stack spacing={0.25}>
        <Typography
            fontSize="11px"
            fontWeight={600}
            color="rgba(255,255,255,0.7)"
            textTransform="uppercase"
            letterSpacing="0.5px"
        >
            {label}
        </Typography>
        <Typography
            className="no-translation"
            fontSize="34px"
            fontWeight={700}
            color="white"
            fontFamily={FONTS.display}
            lineHeight={1.1}
        >
            €{amount}
        </Typography>
    </Stack>
);

// Light inline stat — value over label, no glass box, shrinks freely (minWidth
// 0) so three fit any width and long RU labels wrap instead of clipping.
const MiniStat = ({ value, label }: { value: string; label: string }) => (
    <Stack flex={1} minWidth={0} spacing={0.1}>
        <Typography
            className="no-translation"
            fontFamily={FONTS.display}
            fontWeight={700}
            fontSize="19px"
            color="white"
            lineHeight={1.1}
        >
            {value}
        </Typography>
        <Typography
            fontSize="11px"
            fontWeight={500}
            color="rgba(255,255,255,0.65)"
            lineHeight={1.25}
        >
            {label}
        </Typography>
    </Stack>
);

const DashboardHero: React.FC<DashboardHeroProps> = ({
    overview,
    setupProgress,
}) => {
    const todayFormatted = dayjs().format("dddd, MMMM D");

    const today = overview?.today;
    const bookings = (today?.done ?? 0) + (today?.waiting ?? 0);
    const privateSlots = today?.blocked ?? 0;
    const totalEarnings = Math.round(
        (today?.received ?? 0) + (today?.expected ?? 0)
    );
    const totalMinutes =
        (today?.hoursReceived ?? 0) + (today?.hoursExpected ?? 0);

    const monthEarned = Math.round(overview?.month?.received ?? 0);

    // Is today a non-working day? Read the flag off the week strip.
    const todayStr = dayjs().format("YYYY-MM-DD");
    const isDayOff =
        overview?.week?.days?.find((d) => d.date === todayStr)?.isWorkingDay ===
        false;

    // Emotional-design rule (agreed with product):
    //   1. Money today wins — always show it, even on a day off.
    //   2. No money + day off  → rest, no numbers.
    //   3. No money + month has earnings → pivot the anchor to the month.
    //   4. No money anywhere yet → a warm open-day state.
    // We never render a bare, demoralizing daily €0.
    // "loading" until `overview` resolves — otherwise the undefined state would
    // flash the "fresh" message before settling on the real one.
    // "off" hides every earnings state block, leaving the greeting-only hero —
    // used when the analytics feature is gated off (FEATURE-002 not yet live).
    const state: "loading" | "money" | "dayoff" | "month" | "fresh" | "off" =
        !ANALYTICS_ENABLED
            ? "off"
            : !overview
            ? "loading"
            : totalEarnings > 0
            ? "money"
            : isDayOff
            ? "dayoff"
            : monthEarned > 0
            ? "month"
            : "fresh";

    return (
        <Stack spacing={2} py={2}>
            {/* Greeting + date — frosted glass card (home shell, unchanged) */}
            <Stack
                spacing={0.5}
                sx={{ ...GLASS_SX, borderRadius: "16px", px: 2, py: 1.5 }}
            >
                <Typography
                    fontWeight={700}
                    fontSize="28px"
                    lineHeight={1.2}
                    color="white"
                    fontFamily={FONTS.display}
                >
                    {getGreeting()}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontSize="14px" color="rgba(255,255,255,0.8)">
                        {todayFormatted}
                    </Typography>
                    {setupProgress &&
                        setupProgress.done < setupProgress.total && (
                            <Box
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    borderRadius: "12px",
                                    px: 1.25,
                                    py: 0.25,
                                }}
                            >
                                <Typography
                                    fontSize="12px"
                                    fontWeight={600}
                                    color="white"
                                >
                                    {setupProgress.done}/{setupProgress.total}{" "}
                                    steps
                                </Typography>
                            </Box>
                        )}
                </Stack>
            </Stack>

            {/* Loading — a quiet glass placeholder holds the space so no wrong
                state flashes and the sheet below doesn't jump. */}
            {state === "loading" && (
                <Box sx={{ ...CARD_SX, minHeight: 104, opacity: 0.5 }} />
            )}

            {/* State 1 — money today (headline € + inline stats). Wins always. */}
            {state === "money" && (
                <Stack spacing={1.25} sx={CARD_SX}>
                    <MoneyHeadline label="Today's total" amount={totalEarnings} />
                    <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.2)" }} />
                    <Stack direction="row" spacing={1.5}>
                        <MiniStat
                            value={`${bookings}`}
                            label={bookings === 1 ? "booking" : "bookings"}
                        />
                        <MiniStat
                            value={formatHours(totalMinutes)}
                            label="hours"
                        />
                        <MiniStat
                            value={`${privateSlots}`}
                            label={
                                privateSlots === 1
                                    ? "private slot"
                                    : "private slots"
                            }
                        />
                    </Stack>
                </Stack>
            )}

            {/* State 2 — day off. Rest, no numbers. */}
            {state === "dayoff" && (
                <Stack spacing={0.5} sx={CARD_SX}>
                    <Typography
                        fontSize="20px"
                        fontWeight={700}
                        color="white"
                        fontFamily={FONTS.display}
                    >
                        Enjoy your day off
                    </Typography>
                    <Typography fontSize="13px" color="rgba(255,255,255,0.8)">
                        Nothing on the calendar — rest up, back at it soon.
                    </Typography>
                </Stack>
            )}

            {/* State 3 — quiet working day: anchor to the month, not a daily €0. */}
            {state === "month" && (
                <Stack spacing={1} sx={CARD_SX}>
                    <MoneyHeadline label="This month" amount={monthEarned} />
                    <Typography fontSize="13px" color="rgba(255,255,255,0.8)">
                        Nothing booked today yet — the month's still adding up.
                    </Typography>
                </Stack>
            )}

            {/* State 4 — fresh master, no earnings anywhere yet. */}
            {state === "fresh" && (
                <Stack spacing={0.5} sx={CARD_SX}>
                    <Typography
                        fontSize="20px"
                        fontWeight={700}
                        color="white"
                        fontFamily={FONTS.display}
                    >
                        Your day is open
                    </Typography>
                    <Typography fontSize="13px" color="rgba(255,255,255,0.8)">
                        Your earnings will show here as bookings come in.
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
};

export default DashboardHero;
